/**
 * Fills "from N backers" under each ProBro card raised badge.
 * Uses GET /apps/probros/backers?handle=…&limit=1 (response includes backer_count).
 * Hidden when count is 0 or the proxy fails.
 */

const ENDPOINT = '/apps/probros/backers';

/** @type {Map<string, Promise<number>>} */
const cache = new Map();

/**
 * @param {string} handle
 * @returns {Promise<number>}
 */
function fetchBackerCount(handle) {
  const key = handle.toLowerCase();
  const cached = cache.get(key);
  if (cached) return cached;

  const promise = (async () => {
    const url = new URL(ENDPOINT, window.location.origin);
    url.searchParams.set('handle', key);
    url.searchParams.set('limit', '1');

    const response = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.ok) return 0;
    return Number(data.backer_count) || 0;
  })().catch(() => 0);

  cache.set(key, promise);
  return promise;
}

/**
 * @param {number} count
 * @returns {string}
 */
function backersLabel(count) {
  return `from ${count} backer${count === 1 ? '' : 's'}`;
}

/**
 * @param {Element} root
 */
async function hydrate(root = document) {
  const nodes = root.querySelectorAll('[data-pb-backers-handle]');
  await Promise.all(
    [...nodes].map(async (row) => {
      const handle = (row.getAttribute('data-pb-backers-handle') || '').trim();
      const target = row.querySelector('[data-pb-backers-label]');
      if (!handle || !target) return;

      const count = await fetchBackerCount(handle);
      if (count > 0) {
        target.textContent = backersLabel(count);
        target.hidden = false;
      } else {
        target.textContent = '';
        target.hidden = true;
      }
    }),
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => hydrate(), { once: true });
} else {
  hydrate();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (section instanceof Element) hydrate(section);
});
